/**
 * Utility functions and hooks for accessing spacing configuration.
 */

import { useSiteConfig } from "@/contexts/SiteConfigContext";
import {
  DEFAULT_SPACING_CONFIG,
  getSpacingFromConfig,
  type SpacingClasses,
} from "./spacingConfig";
import type { GlobalSpacingConfig } from "@/types/config";

/**
 * Hook to access spacing configuration from SiteConfig.
 * Returns spacing classes based on the global preset.
 *
 * @returns Spacing classes for sections and blocks
 */
export function useSpacingConfig(): SpacingClasses {
  const { config } = useSiteConfig();
  const spacingConfig = config.spacingConfig || DEFAULT_SPACING_CONFIG;

  // If global spacing is disabled, still return default for consistency
  // Individual blocks can choose whether to apply these classes
  return getSpacingFromConfig(spacingConfig);
}

/**
 * Hook to check if global spacing configuration is enabled.
 *
 * @returns true if global spacing should be applied, false otherwise
 */
export function useIsGlobalSpacingEnabled(): boolean {
  const { config } = useSiteConfig();
  const spacingConfig = config.spacingConfig || DEFAULT_SPACING_CONFIG;
  return spacingConfig.useGlobalConfig;
}

/**
 * Hook to get the full spacing configuration object.
 *
 * @returns GlobalSpacingConfig object
 */
export function useSpacingConfigObject(): GlobalSpacingConfig {
  const { config } = useSiteConfig();
  return config.spacingConfig || DEFAULT_SPACING_CONFIG;
}

/**
 * Combine spacing classes with additional custom classes.
 * Useful for adding one-off spacing adjustments while preserving global config.
 *
 * @param spacingClasses - Base spacing class string
 * @param customClasses - Additional class string
 * @returns Combined class string
 */
export function combineSpacingClasses(
  spacingClasses: string,
  customClasses?: string
): string {
  if (!customClasses) return spacingClasses;
  return `${spacingClasses} ${customClasses}`;
}
